import { useEffect, useState } from 'react';
import { fetchSessionPhases, type SessionPhasesView } from '../api/phases';
import { ca } from '../i18n/ca';

/**
 * A scripted night's phases (engine step E12, ENGINE_DESIGN.md §2.20). On the
 * direction page it is the phase backbone live: every phase of the script,
 * which fired and when, what fired it, the one the night is in now, and the
 * script's other events. In the report, once the night is over, it is the
 * «Fases» section: the phases that fired, in order, with their minute. Who
 * sees what is the platform's decision (`/phases` cuts the view by audience,
 * R16); this only draws what it is handed, and draws nothing for a night with
 * no script or a view with nothing in it.
 *
 * `trigger` is anything that changes when the bus has moved (the count of
 * script events the page holds): a change asks the platform again.
 */
export function ScriptPhases({
  code,
  trigger = 0,
  live = false,
}: {
  code: string;
  trigger?: number;
  live?: boolean;
}) {
  const [view, setView] = useState<SessionPhasesView | null>(null);

  useEffect(() => {
    let alive = true;
    fetchSessionPhases(code)
      .then((v) => alive && setView(v))
      .catch(() => alive && setView(null));
    return () => {
      alive = false;
    };
  }, [code, trigger]);

  if (!view || view.audience === 'NONE') return null;
  if (!view.scripted && view.phases.length === 0) return null;
  const direction = view.audience === 'DIRECTION';
  const t = ca.scriptPhases;
  const title = direction ? t.directionTitle : t.reportTitle;
  const anyFired = view.phases.some((p) => p.fired);

  return (
    <section className="card" aria-label={title}>
      <h2>{title}</h2>
      <p className="muted small">{direction ? t.directionHint : t.reportHint}</p>
      {!anyFired && <p className="muted small">{t.none}</p>}
      <ol className="activity">
        {view.phases.map((p) => {
          const current = live && p.id === view.current;
          return (
            <li key={p.id} className={p.fired ? undefined : 'muted'} data-phase={p.id}>
              {p.fired && p.at !== null ? (
                <span className="muted small num">{t.minute(p.at)}</span>
              ) : (
                <span className="muted small">{t.pending}</span>
              )}{' '}
              <strong>{p.title}</strong>
              {current && (
                <>
                  {' '}
                  <span className="chip live">
                    <span className="dot live" aria-hidden="true" />
                    {t.now}
                  </span>
                </>
              )}
              {p.count > 1 && <span className="muted small"> {t.repeats(p.count)}</span>}
              {p.firedBy && <span className="muted small"> · {t.firedBy(p.firedBy)}</span>}
              {p.alarm === 'up' && <span className="muted small"> · {t.alarmUp}</span>}
            </li>
          );
        })}
      </ol>
      {direction && view.events.length > 0 && (
        <details>
          <summary>{t.eventsTitle}</summary>
          <ul className="activity">
            {[...view.events].reverse().map((e, i) => (
              <li key={`${e.name}-${e.at}-${i}`}>
                <span className="muted small num">{t.minute(e.at)}</span> {t.event(e.name)}
                {e.count > 1 && <span className="muted small"> {t.repeats(e.count)}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
